import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Clock, Volume2, VolumeX } from "lucide-react";
import { CONTACT } from "@/veedu/data";
import { useOpeningHours } from "@/veedu/openingHours";
import { scrollToId } from "@/hooks/useLenis";
import { useAmbientAudio } from "@/context/AmbientAudioContext";

/* ─────────────────────────────────────────────────────────────
   MAIN NAVIGATION & FULL-SCREEN ARCHITECTURAL PLAN OVERLAY
───────────────────────────────────────────────────────────── */
const Nav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(location.pathname !== "/");
  const status = useOpeningHours();
  const { isPlaying, isMuted, toggleMute } = useAmbientAudio();

  useEffect(() => {
    const handleScroll = () => {
      if (location.pathname !== "/") {
        setScrolled(window.scrollY > 40);
        return;
      }
      const hero = document.getElementById("entrance");
      // Keep navbar transparent throughout the hero section; activate dark backdrop when scrolling to other sections
      const threshold = hero ? hero.offsetHeight * 0.65 : window.innerHeight * 0.65;
      setScrolled(window.scrollY > threshold);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    let unbindLenis;
    if (window.__lenis) {
      unbindLenis = window.__lenis.on("scroll", ({ scroll }) => {
        if (location.pathname !== "/") {
          setScrolled(scroll > 40);
          return;
        }
        const hero = document.getElementById("entrance");
        const threshold = hero ? hero.offsetHeight * 0.65 : window.innerHeight * 0.65;
        setScrolled(scroll > threshold);
      });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (typeof unbindLenis === "function") unbindLenis();
    };
  }, [location.pathname]);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => {
        scrollToId(id);
      }, 150);
    } else {
      scrollToId(id);
    }
  };

  return (
    <>
      {/* Persistent top header — transparent on hero/landing, dark glassmorphism on other sections */}
      <header
        className={`fixed inset-x-0 top-0 z-[120] flex items-start justify-between px-3 sm:px-6 md:px-10 transition-all duration-500 pointer-events-auto ${
          scrolled
            ? "bg-[#0e0906]/95 backdrop-blur-md border-b border-brass/25 shadow-[0_8px_30px_rgba(0,0,0,0.6)] py-1.5 sm:py-3 md:py-3.5"
            : "bg-transparent border-b border-transparent shadow-none py-2 sm:py-4 md:py-5"
        }`}
      >
        {/* Left column: #VEEDU brand mark with live timing badge below */}
        <div className="flex flex-col items-start gap-0.5 sm:gap-1.5 shrink-0">
          <button
            data-testid="wordmark-btn"
            onClick={() => go("entrance")}
            className="flex items-baseline gap-1 sm:gap-3 shrink-0 transition-opacity hover:opacity-80 cursor-pointer text-left"
          >
            <span className="font-display text-[15px] sm:text-2xl tracking-[0.14em] sm:tracking-[0.18em] text-ivory leading-tight">#VEEDU</span>
            <span className="font-mal text-[9.5px] sm:text-sm text-brass/80">വീട്</span>
          </button>

          {/* Live Opening Status Chip — Visible on all screens, under #VEEDU */}
          <div
            data-testid="nav-status-badge"
            className="flex items-center gap-1 sm:gap-2 px-2 py-[1.5px] sm:px-3 sm:py-1 rounded-full border border-brass/25 bg-black/50 backdrop-blur-sm text-ivory text-[8px] sm:text-[11.5px] md:text-[12px] font-body tracking-[0.05em] sm:tracking-[0.12em] uppercase shrink-0 shadow-sm"
          >
            <span
              className={`h-1 w-1 sm:h-2 sm:w-2 rounded-full shrink-0 ${
                status.isOpen
                  ? status.isLastOrder
                    ? "bg-amber-400 animate-pulse shadow-[0_0_6px_rgba(251,191,36,0.7)]"
                    : "bg-emerald-400 animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.7)]"
                  : "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.7)]"
              }`}
            />
            <span className="font-medium whitespace-nowrap">{status.headerStatus}</span>
            <span className="opacity-40">•</span>
            <span className="text-brass font-medium whitespace-nowrap">
              <span className="inline sm:hidden">{status.headerDetail.replace(" UK", "")}</span>
              <span className="hidden sm:inline">{status.headerDetail}</span>
            </span>
          </div>
        </div>

        {/* Right side navigation: Ambience Audio Toggle & Floor Plan Trigger */}
        <div className="flex items-center gap-2 sm:gap-3.5 pt-0.5 sm:pt-1 shrink-0">
          {/* Ambient Music Toggle Icon */}
          <button
            type="button"
            onClick={toggleMute}
            data-testid="nav-ambient-audio-btn"
            aria-label={isMuted ? "Unmute meditation ambience" : "Mute meditation ambience"}
            title={isMuted ? "Unmute meditation ambience" : "Mute meditation ambience"}
            className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-brass/30 bg-black/40 hover:border-brass hover:bg-brass/10 text-ivory/80 hover:text-brass transition-all duration-300 active:scale-95 cursor-pointer shadow-sm"
          >
            {isPlaying && !isMuted ? (
              <Volume2 className="w-3.5 h-3.5 text-brass animate-pulse" />
            ) : (
              <VolumeX className="w-3.5 h-3.5 text-ivory/40" />
            )}
          </button>

          <button
            data-testid="menu-trigger-btn"
            onClick={() => setOpen(true)}
            className="font-body uppercase text-ivory/90 transition-colors hover:text-brass flex flex-col items-end sm:flex-row sm:items-center gap-0 sm:gap-1.5 cursor-pointer group text-right"
          >
            <span className="font-medium text-[9px] sm:text-[11.5px] tracking-[0.15em] sm:tracking-[0.25em] whitespace-nowrap group-hover:text-brass transition-colors leading-tight">
              FLOOR PLAN
            </span>
            <span className="text-[7.5px] sm:text-[10px] tracking-normal font-sans font-normal text-brass normal-case opacity-90 group-hover:opacity-100 transition-opacity whitespace-nowrap leading-tight">
              ( Choose your seat )
            </span>
          </button>
        </div>
      </header>

      {/* Full-screen architectural overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-overlay"
            className="fixed inset-0 z-[160] bg-[#0a0603]/[0.98] backdrop-blur-md overflow-hidden flex flex-col justify-between"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.75, ease: [0.76, 0, 0.24, 1] }}
          >
            {/* Top bar header */}
            <div className="flex items-center justify-between px-6 py-5 md:px-10 shrink-0">
              <div className="flex items-center gap-3">
                <span className="font-display text-xs md:text-sm tracking-[0.3em] uppercase text-cream font-semibold">
                  FLOOR PLAN
                </span>
                <span className="text-[10px] md:text-xs tracking-wider text-brass font-body normal-case">
                  ( choose your seat )
                </span>
                <span className="hidden sm:inline-block h-px w-10 bg-brass/40 ml-1" />
              </div>

              {/* Opening Hours Summary Badge in Plan Header */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full border border-brass/30 bg-brass/10 text-brass text-[10px] font-body tracking-wider uppercase">
                <Clock size={12} />
                <span>Tue–Fri: 16:30–23:00 | Sat–Sun: 13:00–23:00 | Mon: Closed</span>
              </div>

              <button
                data-testid="nav-close-btn"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="flex items-center justify-center w-10 h-10 rounded-full border border-cream/20 text-cream/80 transition-all hover:border-brass hover:text-brass hover:scale-105"
              >
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            {/* Main Center Image Display — Ground & First Floor Plan Screenshot */}
            <div className="flex-1 min-h-0 flex items-center justify-center px-4 py-6 md:px-10 md:py-8 overflow-hidden">
              <div className="relative w-full max-w-[1120px] h-full flex items-center justify-center p-1 sm:p-2">
                <img
                  src="/Images/final_veeduplan.jpeg"
                  alt="Chef Jomon's Veedu Restaurant Floor Plan"
                  className="max-w-full max-h-full object-contain pointer-events-none select-none rounded-lg border border-brass/40 shadow-2xl"
                />
              </div>
            </div>

            {/* Bottom Footer Bar with Navigation, Opening Hours & Contact */}
            <div
              className="flex flex-wrap items-center justify-between gap-3 px-6 md:px-10 py-3 border-t border-cream/[0.1] shrink-0"
              style={{ background: "rgba(10,6,3,0.95)" }}
            >
              <div className="flex items-center gap-4 text-cream/70 font-body text-[9.5px] md:text-[10px] tracking-wider uppercase">
                <a href={CONTACT.mobileHref} className="hover:text-brass transition-colors">💬 {CONTACT.mobile}</a>
                <span className="opacity-40">•</span>
                <a href={CONTACT.phoneHref} className="hover:text-brass transition-colors">📞 {CONTACT.phone}</a>
              </div>

              {/* Responsive UK Hours Summary Banner */}
              <div className="flex items-center gap-2 font-body text-[9.5px] tracking-[0.2em] uppercase text-brass">
                <Clock size={12} className="text-brass shrink-0" />
                <span>Tue–Fri: 16:30–23:00 (Last order 22:30) • Sat–Sun: 13:00–23:00 • Mon: Closed</span>
              </div>

              <div className="flex items-center gap-4 font-body text-[9.5px] md:text-[10px] tracking-widest uppercase text-cream/70">
                <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="hover:text-brass transition-colors">Instagram</a>
                <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="hover:text-brass transition-colors">Facebook</a>
                <a href={CONTACT.twitter} target="_blank" rel="noreferrer" className="hover:text-brass transition-colors">X</a>
                <a href={CONTACT.wikipedia} target="_blank" rel="noreferrer" className="hover:text-brass transition-colors">Wikipedia</a>
                <a href={CONTACT.website} target="_blank" rel="noreferrer" className="hover:text-brass transition-colors">Website</a>
                <span className="opacity-40">•</span>
                <a href="https://eveme.uk/product/chef-jomons-attitude-pre-order/" target="_blank" rel="noreferrer" className="hover:text-cream text-brass font-semibold transition-colors">Book: ATTITUDE ↗</a>
                <span className="opacity-40">•</span>
                <a href="/admin/login" className="hover:text-brass text-brass/80 font-semibold transition-colors">Admin</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
